import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button, Table, Input, Tabs } from 'ui';
import { useIntl } from 'react-intl';
import useQueryApiClient from 'utils/useQueryApiClient';
import Filter from 'admin/components/Filter';
import dayjs from 'dayjs';
import { StyledNotifications } from './style';
import type { Pagination } from 'types/Pagination';
import type { NotificationRow, NotificationFilterState } from './types';
import EnumSelect from '../../Selects/EnumSelect';
import DrawerNotification, {
  DatabaseNotification,
} from 'ui/Drawer/Drawers/DrawerNotification';
import NotificationTypeTag, {
  normalizeEnumValues,
} from '../NotificationTypeTag';
import type { TableRowSelection } from 'antd/es/table/interface';
import { Dropdown, MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useRealtimeFarmNotifications } from 'realtime/useRealtimeFarmNotifications';
import { useAuth } from 'hooks/useAuth';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import MobileList from 'admin/components/MobileLayout/MobileList';
import { MobileListField } from 'ui/Card/MobileListCard';
import AdminMobileFilterDrawer from 'ui/Drawer/Drawers/AdminMobileFilterDrawer';
import { useBreakpoint } from 'utils/useBreakpoint';

interface NotificationsProps {
  reload?: number;
  onOpenChat?: (recipientFarmId: number) => void;
  onOpenSettings?: () => void;
  isMobileFilterOpen: boolean;
  onMobileFilterOpenChange: (open: boolean) => void;
}

export default function Notifications({
  reload = 0,
  onOpenChat,
  onOpenSettings,
  isMobileFilterOpen,
  onMobileFilterOpenChange,
}: NotificationsProps) {
  const intl = useIntl();
  const { user } = useAuth();
  const farmId = user?.farmId ?? user?.farm?.id;
  const { data: enumData } = useQueryApiClient({
    request: { url: 'api/v2/enums/NotificationType' },
  });

  const notificationEnumValues = useMemo(
    () => normalizeEnumValues(enumData),
    [enumData]
  );

  const [activeTab, setActiveTab] = useState<'notifications' | 'archive'>(
    'notifications'
  );

  const [advancedSearch, setAdvancedSearch] = useState<NotificationFilterState>(
    {
      status: 'all',
      type_group: 'all',
      page_size: 25,
    }
  );

  const [paginationStatus, setPaginationStatus] = useState<Pagination>({
    total: 0,
    page: 1,
    page_size: 25,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<DatabaseNotification | null>(null);

  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [, setSelectedRows] = useState<NotificationRow[]>([]);
  const [stickyTop, setStickyTop] = useState<number>(0);
  const [stuck, setStuck] = useState<boolean>(false);
  const selectionBarRef = React.useRef<HTMLDivElement | null>(null);

  const [reloadTick, setReloadTick] = useState(0);
  const triggerReload = () => setReloadTick((n) => n + 1);
  const { isLgUp } = useBreakpoint();

  const { appendData: markOneRead } = useQueryApiClient({
    request: { url: '/api/v2/notifications/:id/read', method: 'POST' },
    onSuccess: () => {
      triggerReload();
    },
  });

  const { appendData: markBulkUnread } = useQueryApiClient({
    request: { url: '/api/v2/notifications/bulk-unread', method: 'POST' },
    onSuccess: () => {
      triggerReload();
    },
  });

  const { appendData: markViewed } = useQueryApiClient({
    request: { url: '/api/v2/notifications/:id/viewed', method: 'POST' },
    onSuccess: () => {
      triggerReload();
    },
  });

  const { appendData: bulkRead, isLoading: bulkReadLoading } =
    useQueryApiClient({
      request: { url: '/api/v2/notifications/bulk-read', method: 'POST' },
      onSuccess: () => {
        setSelectedKeys([]);
        setSelectedRows([]);
        triggerReload();
      },
    });

  const { appendData: archiveOne } = useQueryApiClient({
    request: { url: '/api/v2/notifications/:id/archive', method: 'POST' },
    onSuccess: () => {
      triggerReload();
    },
  });

  const { appendData: unarchiveOne } = useQueryApiClient({
    request: { url: '/api/v2/notifications/:id/unarchive', method: 'POST' },
    onSuccess: () => {
      triggerReload();
    },
  });

  const { appendData: bulkArchive, isLoading: bulkArchiveLoading } =
    useQueryApiClient({
      request: { url: '/api/v2/notifications/bulk-archive', method: 'POST' },
      onSuccess: () => {
        setSelectedKeys([]);
        setSelectedRows([]);
        triggerReload();
      },
    });

  const { appendData: bulkUnarchive, isLoading: bulkUnarchiveLoading } =
    useQueryApiClient({
      request: { url: '/api/v2/notifications/bulk-unarchive', method: 'POST' },
      onSuccess: () => {
        setSelectedKeys([]);
        setSelectedRows([]);
        triggerReload();
      },
    });

  const onMarkOne = (id: string) => {
    markOneRead(null, { id });
  };

  const onMarkUnread = (id: string) => {
    markBulkUnread({ ids: [id] });
  };

  const onArchiveOne = (id: string) => {
    archiveOne(null, { id });
  };

  const onUnarchiveOne = (id: string) => {
    unarchiveOne(null, { id });
  };

  const onBulkRead = () => {
    if (!selectedKeys.length) return;
    bulkRead({ ids: selectedKeys });
    setSelectedKeys([]);
    setSelectedRows([]);
  };

  const onBulkArchiveToggle = () => {
    if (!selectedKeys.length) return;
    if (activeTab === 'archive') {
      bulkUnarchive({ ids: selectedKeys });
    } else {
      bulkArchive({ ids: selectedKeys });
    }
    setSelectedKeys([]);
    setSelectedRows([]);
  };

  useEffect(() => {
    if (reload > 0) triggerReload();
  }, [reload]);

  const tableFilter = useMemo(() => {
    const {
      status = 'all',
      search = '',
      type_group = 'all',
    } = advancedSearch;

    const filter: Record<string, any> = {
      status,
      archived: activeTab === 'archive' ? 'archived' : 'active',
    };
    const trimmed = search.trim();
    if (trimmed) filter.search = trimmed;
    if (type_group !== 'all') filter.type_group = type_group;

    return filter;
  }, [
    advancedSearch.status,
    advancedSearch.search,
    advancedSearch.type_group,
    activeTab,
  ]);

  useEffect(() => {
    setSelectedKeys([]);
    setSelectedRows([]);
    triggerReload();
  }, [activeTab]);

  const handleRealtimeEvent = useCallback(() => {
    triggerReload();
  }, []);

  useRealtimeFarmNotifications({
    farmId,
    eventName: '.farm.notification',
    onEvent: handleRealtimeEvent,
  });

  useRealtimeFarmNotifications({
    farmId,
    eventName: '.notification.updated',
    onEvent: handleRealtimeEvent,
  });

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: 'general.created_at',
        }),
        dataIndex: 'created_at',
        render: (v: string, row: NotificationRow) => {
          const formatted = v ? dayjs(v).format('DD.MM.YYYY | HH:mm') : '-';
          return (
            <span style={{ fontWeight: !row.is_read ? 700 : 400 }}>
              {formatted}
            </span>
          );
        },
      },
      {
        title: intl.formatMessage({
          id: 'notifications.title',
        }),
        dataIndex: ['data', 'title'],
        render: (v: string, row: NotificationRow) => {
          const text = v?.trim?.() ? v : '-';
          return (
            <span style={{ fontWeight: !row.is_read ? 700 : 400 }}>{text}</span>
          );
        },
      },
      {
        title: intl.formatMessage({
          id: 'notification.status',
        }),
        dataIndex: 'read_at',
        render: (v: string | null) =>
          v
            ? intl.formatMessage({
                id: 'notification.status.read',
              })
            : intl.formatMessage({
                id: 'general.unread',
              }),
      },
      {
        title: '',
        dataIndex: ['data', 'type'],
        render: (short: string | undefined, row: NotificationRow) => (
          <NotificationTypeTag
            shortType={short}
            fallbackType={row.type}
            enumValues={notificationEnumValues}
          />
        ),
      },
      {
        title: (
          <div className="actions-title">
            {intl.formatMessage({
              id: 'general.actions',
            })}
          </div>
        ),
        key: 'actions',
        render: (row: NotificationRow) => {
          const menuItems: MenuProps['items'] = [];

          if (!row.read_at) {
            menuItems.push({
              label: intl.formatMessage({
                id: 'notification.mark_as_read',
              }),
              key: 'mark-as-read',
            });
          }

          if (row.read_at) {
            menuItems.push({
              label: intl.formatMessage({
                id: 'notification.mark_as_unread',
              }),
              key: 'mark-as-unread',
            });
          }

          if (activeTab === 'archive') {
            menuItems.push({
              label: intl.formatMessage({
                id: 'notification.unarchive',
              }),
              key: 'unarchive',
            });
          } else {
            menuItems.push({
              label: intl.formatMessage({
                id: 'notification.archive',
              }),
              key: 'archive',
            });
          }

          return (
            <Dropdown
              menu={{
                items: menuItems,
                onClick: (e) => {
                  e.domEvent?.stopPropagation?.();
                  switch (e.key) {
                    case 'mark-as-read':
                      onMarkOne(row.id);
                      break;
                    case 'mark-as-unread':
                      onMarkUnread(row.id);
                      break;
                    case 'archive':
                      onArchiveOne(row.id);
                      break;
                    case 'unarchive':
                      onUnarchiveOne(row.id);
                      break;
                  }
                },
              }}
              trigger={['click']}
            >
              <Button
                className="action"
                label={intl.formatMessage({
                  id: 'general.actions',
                })}
                icon={<DownOutlined />}
                iconPosition="end"
                size="middle"
                onClick={(ev: React.MouseEvent) => ev.stopPropagation()}
              />
            </Dropdown>
          );
        },
      },
    ],
    [intl, activeTab, notificationEnumValues]
  );

  const handleNotificationClick = useCallback(
    (record: DatabaseNotification) => {
      if ((record as NotificationRow)?.is_read === false) {
        markViewed(null, { id: record.id });
        setSelectedRows((rows) =>
          rows.map((r) => (r.id === record.id ? { ...r, is_read: true } : r))
        );
        triggerReload();
      }

      const notificationData =
        typeof record.data === 'string'
          ? (() => {
              try {
                return JSON.parse(record.data);
              } catch {
                return {};
              }
            })()
          : record.data;

      const shortType = notificationData?.type || '';
      const isMessageType = shortType === 'messages.sent';

      if (isMessageType && notificationData?.sender_farm_id && onOpenChat) {
        onOpenChat(notificationData.sender_farm_id);
      } else {
        setSelected(record);
        setDrawerOpen(true);
        if ((record as NotificationRow)?.is_read === false) {
          setSelected((prev) => (prev ? { ...prev, is_read: true } : prev));
        }
      }
    },
    [onOpenChat]
  );

  const onRow = (record: DatabaseNotification) => ({
    onClick: () => handleNotificationClick(record),
  });

  const mobileItems: {
    coverFields: MobileListField[];
    bodyFields: MobileListField[];
    bodyTripleSectionFields: MobileListField[];
    footerFields: MobileListField[];
  } = useMemo(
    () => ({
      coverFields: [
        {
          name: 'header',
          render: (_: unknown, record: NotificationRow) => {
            const data = record.data;
            const shortType =
              typeof data === 'object' && data?.type ? data.type : record.type;
            return (
              <div className="mobile-notification-header">
                <span>
                  {record.created_at
                    ? dayjs(record.created_at).format('DD.MM.YYYY | HH:mm')
                    : '-'}
                </span>
                <NotificationTypeTag
                  shortType={shortType}
                  fallbackType={record.type}
                  enumValues={notificationEnumValues}
                  size="sm"
                />
              </div>
            );
          },
        },
      ],
      bodyFields: [
        {
          name: 'data',
          label: intl.formatMessage({ id: 'notifications.title' }),
          render: (_: unknown, record: NotificationRow) => {
            const data = record.data;
            const title =
              typeof data === 'object' && data?.title
                ? String(data.title).trim()
                : '-';
            return (
              <span className="mobile-notification-message">
                {title || '-'}
              </span>
            );
          },
        },
      ],
      bodyTripleSectionFields: [
        {
          name: 'read_at',
          label: intl.formatMessage({ id: 'notification.status' }),
          render: (v: string | null) =>
            v
              ? intl.formatMessage({ id: 'notification.status.read' })
              : intl.formatMessage({ id: 'general.unread' }),
        },
      ],
      footerFields: [
        {
          name: 'actions',
          render: (_: unknown, record: NotificationRow) => {
            const menuItems: MenuProps['items'] = [];

            menuItems.push({
              label: intl.formatMessage({ id: 'general.view' }),
              key: 'view',
            });

            if (!record.read_at) {
              menuItems.push({
                label: intl.formatMessage({
                  id: 'notification.mark_as_read',
                }),
                key: 'mark-as-read',
              });
            }

            if (record.read_at) {
              menuItems.push({
                label: intl.formatMessage({
                  id: 'notification.mark_as_unread',
                }),
                key: 'mark-as-unread',
              });
            }

            if (activeTab === 'archive') {
              menuItems.push({
                label: intl.formatMessage({
                  id: 'notification.unarchive',
                }),
                key: 'unarchive',
              });
            } else {
              menuItems.push({
                label: intl.formatMessage({
                  id: 'notification.archive',
                }),
                key: 'archive',
              });
            }

            return (
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown
                  menu={{
                    items: menuItems,
                    onClick: (e) => {
                      e.domEvent?.stopPropagation?.();
                      switch (e.key) {
                        case 'view':
                          handleNotificationClick(record);
                          break;
                        case 'mark-as-read':
                          onMarkOne(record.id);
                          break;
                        case 'mark-as-unread':
                          onMarkUnread(record.id);
                          break;
                        case 'archive':
                          onArchiveOne(record.id);
                          break;
                        case 'unarchive':
                          onUnarchiveOne(record.id);
                          break;
                      }
                    },
                  }}
                  trigger={['click']}
                >
                  <Button
                    className="action"
                    label={intl.formatMessage({
                      id: 'general.actions',
                    })}
                    icon={<DownOutlined />}
                    iconPosition="end"
                    size="small"
                  />
                </Dropdown>
              </div>
            );
          },
        },
      ],
    }),
    [
      intl,
      activeTab,
      notificationEnumValues,
      handleNotificationClick,
      onMarkOne,
      onMarkUnread,
      onArchiveOne,
      onUnarchiveOne,
    ]
  );

  const tabContent = (
    <ResponsiveWrapper
      desktopComponent={
        <>
          <Filter
            onFinish={(values: Record<string, string | number | undefined>) => {
              const next: NotificationFilterState = {
                search: (values.search as string) || '',
                status: (values.status as 'all' | 'new' | 'read') || 'all',
                type_group:
                  (values.type_group as 'all' | 'listings' | 'offers') || 'all',
                page_size: values.page_size
                  ? Number(values.page_size)
                  : paginationStatus.page_size || 25,
              };
              setAdvancedSearch(next);
              setSelectedKeys([]);
              setSelectedRows([]);
            }}
            totalRecord={paginationStatus?.total ?? 0}
            isLabelVisible={true}
            tagLabelPrefix="notifications.filters"
            tagEnumPrefixes={{
              type_group: 'NotificationTypeGroup',
              status: 'NotificationStatus',
            }}
          >
            <Input
              label={intl.formatMessage({
                id: 'general.search',
              })}
              name="search"
              placeholder={intl.formatMessage({
                id: 'general.search',
              })}
              allowClear
            />

            <EnumSelect
              label={intl.formatMessage({
                id: 'notification.status',
              })}
              name="status"
              code="NotificationStatus"
            />

            <EnumSelect
              label={intl.formatMessage({
                id: 'notification.filter_by_type',
              })}
              name="type_group"
              code="NotificationTypeGroup"
            />

            <Button
              type="primary"
              label={intl.formatMessage({
                id: 'listings.apply_filter',
              })}
              htmlType="submit"
            />
          </Filter>

          {selectedKeys.length > 0 && (
            <div
              ref={selectionBarRef}
              className={`selection-bar ${stuck ? 'is-stuck' : ''}`}
              style={
                {
                  '--sticky-offset': `${stickyTop}px`,
                } as React.CSSProperties
              }
            >
              <div className="left">
                {intl.formatMessage(
                  { id: 'general.selected_n' },
                  { count: selectedKeys.length }
                )}
              </div>
              <div className="right">
                <Button
                  size="small"
                  type="primary"
                  label={intl.formatMessage({
                    id: 'notification.mark_selected_as_read',
                  })}
                  onClick={onBulkRead}
                  loading={bulkReadLoading}
                />

                <Button
                  size="small"
                  type="default"
                  label={
                    activeTab === 'archive'
                      ? intl.formatMessage({
                          id: 'notification.unarchive_selected',
                        })
                      : intl.formatMessage({
                          id: 'notification.archive_selected',
                        })
                  }
                  onClick={onBulkArchiveToggle}
                  loading={
                    activeTab === 'archive'
                      ? bulkUnarchiveLoading
                      : bulkArchiveLoading
                  }
                />

                <Button
                  size="small"
                  label={intl.formatMessage({
                    id: 'general.clear',
                  })}
                  onClick={() => {
                    setSelectedKeys([]);
                    setSelectedRows([]);
                  }}
                />
              </div>
            </div>
          )}

          <Table
            url="/api/v2/notifications"
            columns={columns}
            filter={tableFilter}
            rowKey="id"
            paginnationStatus={setPaginationStatus}
            onRow={onRow}
            rowClassName={(record) =>
              `is-clickable ${(record as NotificationRow)?.is_read === false ? 'is-unread' : ''}`
            }
            enableSelectedRow
            rowSelectionFunction={useMemo<TableRowSelection<NotificationRow>>(
              () => ({
                selectedRowKeys: selectedKeys,
                onChange: (keys, rows) => {
                  setSelectedKeys(keys);
                  setSelectedRows(rows as NotificationRow[]);
                },
                preserveSelectedRowKeys: false,
              }),
              [selectedKeys]
            )}
            reload={reloadTick}
          />
        </>
      }
      mobileComponent={
        <MobileList
          className="notifications-mobile-list"
          url="/api/v2/notifications"
          filter={tableFilter}
          reload={reloadTick}
          rowKey="id"
          coverFields={mobileItems.coverFields}
          bodyFields={mobileItems.bodyFields}
          bodyTripleSectionFields={mobileItems.bodyTripleSectionFields}
          footerFields={mobileItems.footerFields}
          paginnationStatus={setPaginationStatus}
          page_size={advancedSearch.page_size ?? 25}
        />
      }
    />
  );

  const tabItems = [
    {
      key: 'notifications',
      label: intl.formatMessage({
        id: 'navigation.notifications',
      }),
      children: tabContent,
    },
    {
      key: 'archive',
      label: intl.formatMessage({
        id: 'navigation.notification_archive',
      }),
      children: tabContent,
    },
  ];

  useEffect(() => {
    const header =
      (document.querySelector('.ant-layout-header') as HTMLElement) ||
      (document.querySelector('.page-header') as HTMLElement) ||
      null;

    const offset = (header?.offsetHeight ?? 0) + 8;
    setStickyTop(offset);

    if (selectionBarRef.current) {
      selectionBarRef.current.style.setProperty(
        '--sticky-offset',
        `${offset}px`
      );
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const el = selectionBarRef.current;
      if (!el) return;

      const rectTop = el.getBoundingClientRect().top;
      const threshold =
        parseInt(getComputedStyle(el).getPropertyValue('--sticky-offset')) ||
        stickyTop;

      const nowStuck = rectTop <= threshold + 1;
      if (nowStuck !== stuck) setStuck(nowStuck);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [stickyTop, stuck]);

  const notificationsFilter = (
    <Filter
      onFinish={(values: Record<string, string | number | undefined>) => {
        const next: NotificationFilterState = {
          search: (values.search as string) || '',
          status: (values.status as 'all' | 'new' | 'read') || 'all',
          type_group:
            (values.type_group as 'all' | 'listings' | 'offers') || 'all',
          page_size: values.page_size
            ? Number(values.page_size)
            : paginationStatus.page_size || 25,
        };
        setAdvancedSearch(next);
        setSelectedKeys([]);
        setSelectedRows([]);
      }}
      totalRecord={paginationStatus?.total ?? 0}
      isLabelVisible={true}
      tagLabelPrefix="notifications.filters"
      tagEnumPrefixes={{
        type_group: 'NotificationTypeGroup',
        status: 'NotificationStatus',
      }}
    >
      <Input
        label={intl.formatMessage({
          id: 'general.search',
        })}
        name="search"
        placeholder={intl.formatMessage({
          id: 'general.search',
        })}
        allowClear
      />

      <EnumSelect
        label={intl.formatMessage({
          id: 'notification.status',
        })}
        name="status"
        code="NotificationStatus"
      />

      <EnumSelect
        label={intl.formatMessage({
          id: 'notification.filter_by_type',
        })}
        name="type_group"
        code="NotificationTypeGroup"
      />

      <div className={'button-section-container'}>
        <div className={'button-section-wrapper'}>
          <Button
            className={'mobile-button'}
            type="link"
            label={intl.formatMessage({ id: 'general.delete_filter' })}
            onClick={() => {
              setAdvancedSearch({
                status: 'all',
                type_group: 'all',
                page_size: 25,
              });
              setSelectedKeys([]);
              setSelectedRows([]);
              triggerReload();
            }}
          />
          <Button
            className={'mobile-button'}
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={() => onMobileFilterOpenChange(false)}
          />
          <Button
            type="primary"
            label={
              isLgUp
                ? intl.formatMessage({ id: 'listings.apply_filter' })
                : intl.formatMessage({ id: 'general.filter_action' })
            }
            htmlType="submit"
            onClick={() => onMobileFilterOpenChange(false)}
          />
        </div>
      </div>
    </Filter>
  );

  return (
    <>
      <StyledNotifications>
        <Tabs
          items={tabItems}
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'notifications' | 'archive')}
          tabBarExtraContent={
            onOpenSettings && (
              <Button
                className="settings-btn hide-mobile"
                label={intl.formatMessage({
                  id: 'notifications.open_settings',
                })}
                onClick={onOpenSettings}
                icon={
                  <img
                    src="/svgs/admin/default-admin-icon.svg"
                    width={23}
                    height={23}
                    alt=""
                  />
                }
                iconPosition="end"
              />
            )
          }
        />

        <DrawerNotification
          open={drawerOpen}
          notification={selected}
          onClose={() => setDrawerOpen(false)}
          onMarkedRead={() => {
            triggerReload();
            setSelected((prev) =>
              prev ? { ...prev, read_at: new Date().toISOString() } : prev
            );
          }}
          onMarkedUnread={() => {
            triggerReload();
            setSelected((prev) => (prev ? { ...prev, read_at: null } : prev));
          }}
          onArchived={() => {
            triggerReload();
            setSelected((prev) =>
              prev ? { ...prev, archived_at: new Date().toISOString() } : prev
            );
          }}
          onUnarchived={() => {
            triggerReload();
            setSelected((prev) =>
              prev ? { ...prev, archived_at: null } : prev
            );
          }}
        />
      </StyledNotifications>

      <AdminMobileFilterDrawer
        isOpen={isMobileFilterOpen}
        onClose={() => onMobileFilterOpenChange(false)}
      >
        {notificationsFilter}
      </AdminMobileFilterDrawer>
    </>
  );
}
